import React, { useEffect, useMemo, useState } from 'react';

import { useMutation, useQueryClient, useQuery as useTanstackQuery } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';
import { Controller, useForm } from 'react-hook-form';
import { useHistory, useLocation, useParams } from 'react-router-dom';
import { v4 as uuid } from 'uuid';
import { currencyCodes } from '~/globals';
import dayjs from '~/timeInit';

import {
  Alert,
  AlertColor,
  AlertTitle,
  Autocomplete,
  Box,
  Checkbox,
  Chip,
  Container,
  FormControlLabel,
  Stack,
  TextField,
} from '@mui/material';

import PageHeader from '~/components/Common/Elements/PageHeader';
import PageSubheader from '~/components/Common/Elements/PageSubheader';
import Spinner from '~/components/Common/Spinner';
import DebugModal from '~/components/DebugModal/DebugModal';
import usePromoMeta from '~/components/Promo/hooks/usePromoMeta';
import VoucherConditions from '~/components/Vouchers/VoucherConditions';
import VoucherRecentRequests from '~/components/Vouchers/VoucherRecentRequests';
import VoucherRewards from '~/components/Vouchers/VoucherRewards';
import VoucherUsers from '~/components/Vouchers/VoucherUsers';

import VoucherFormNavigation from '~/pages/Vouchers/VoucherFormNavigation';

import { useCreateVoucherUsers } from '~/hooks/apis/promo/useCreateVoucherUsers';
import useCurrentBrand from '~/hooks/useCurrentBrand';

import {
  CreateVoucherPayload,
  CreateVoucherResponse,
  UpdateVoucherPayload,
  UpdateVoucherResponse,
  Voucher,
  createVoucher,
  getVoucher,
  updateVoucher,
} from '~/services/PromoService';

import { normalizeToISODate } from '~/utils/dateFormatter';

type RouteParams = {
  voucherId?: string;
};

type VoucherReward = NonNullable<CreateVoucherPayload['voucher_rewards']>[number];
type VoucherCondition = NonNullable<CreateVoucherPayload['voucher_conditions']>[number];
type VoucherToggleValue = string;
type VoucherToggleOption = {
  key: string;
  isActive: boolean;
  internalDescription: string;
  internalDescriptionLong: string;
  isBeingDeprecated?: boolean;
};
type VoucherWarning = {
  code?: string;
  message?: string;
  level?: string;
};

type VoucherApiFields = {
  voucherName?: string;
  voucherTitle?: string;
  voucherDescription?: string;
  currencyCode?: string;
  voucherRewards?: VoucherReward[];
  voucherConditions?: VoucherCondition[];
  voucherToggles?: VoucherToggleValue[];
  showVoucherFromDate?: string;
  voucher_name?: string;
  voucher_title?: string;
  voucher_description?: string;
  currency_code?: string;
  voucher_rewards?: VoucherReward[];
  voucher_conditions?: VoucherCondition[];
  voucher_toggles?: VoucherToggleValue[];
  show_voucher_from_date?: string;
  voucherWarnings?: VoucherWarning[];
  voucher_warnings?: VoucherWarning[];
};

type VoucherFormValues = {
  voucherName: string;
  voucherTitle: string;
  voucherDescription: string;
  currencyCode: string;
  voucherRewards: VoucherReward[];
  voucherConditions: VoucherCondition[];
  voucherToggles: VoucherToggleValue[];
  showVoucherFromDate: string;
};

const DEFAULT_REWARD_AMOUNT = 50;
const DEFAULT_REWARD_PRODUCTS: Array<VoucherReward['products'][number]> = ['hotel-ALL', 'tour-ALL', 'cruise-ALL'];

const buildDefaultFormValues = (): VoucherFormValues => ({
  voucherName: '',
  voucherTitle: '',
  voucherDescription: '',
  currencyCode: 'AUD',
  voucherRewards: [
    {
      id_voucher_reward: uuid(),
      reward_amount: DEFAULT_REWARD_AMOUNT,
      products: DEFAULT_REWARD_PRODUCTS,
      min_spend: undefined,
      min_spend_products: [],
    },
  ],
  voucherConditions: [],
  voucherToggles: [],
  showVoucherFromDate: '',
});

const parseVoucherApiResponse = (voucher: Voucher): VoucherFormValues => {
  const data = voucher as VoucherApiFields;
  const rawCurrencyCode = data.currencyCode || data.currency_code;
  return {
    voucherName: data.voucherName || data.voucher_name || '',
    voucherTitle: data.voucherTitle || data.voucher_title || '',
    voucherDescription: data.voucherDescription || data.voucher_description || '',
    currencyCode: rawCurrencyCode && currencyCodes.includes(rawCurrencyCode) ? rawCurrencyCode : 'AUD',
    voucherRewards: (data.voucherRewards ?? data.voucher_rewards ?? []).map((reward) => ({
      ...reward,
      id_voucher_reward: reward.id_voucher_reward || uuid(),
    })),
    voucherConditions: (data.voucherConditions ?? data.voucher_conditions ?? []).map((condition) => ({
      ...condition,
      id_voucher_condition: condition.id_voucher_condition || uuid(),
    })),
    voucherToggles: data.voucherToggles ?? data.voucher_toggles ?? [],
    showVoucherFromDate: data.showVoucherFromDate || data.show_voucher_from_date || '',
  };
};

const getVoucherWarningSeverity = (level?: string): AlertColor => {
  if (level === 'error' || level === 'warning' || level === 'info' || level === 'success') {
    return level;
  }
  return 'warning';
};

const hasNonMidnightTime = (isoString: string): boolean => {
  if (!isoString) {
    return false;
  }
  const d = dayjs(isoString);
  return d.hour() !== 0 || d.minute() !== 0 || d.second() !== 0;
};

export default function VoucherFormPage() {
  const { voucherId } = useParams<RouteParams>();
  const isEdit = Boolean(voucherId);
  const history = useHistory();
  const location = useLocation();
  const { enqueueSnackbar } = useSnackbar();
  const queryClient = useQueryClient();

  const { control, handleSubmit, reset, formState, watch, setValue, getValues } = useForm<VoucherFormValues>({
    defaultValues: buildDefaultFormValues(),
  });
  const { isDirty, isSubmitting } = formState;

  const [showAdvanceDescription, setShowAdvanceDescription] = useState(false);
  const [includeShowVoucherFromTime, setIncludeShowVoucherFromTime] = useState(false);
  const [voucherUsers, setVoucherUsers] = useState<string[]>([]);
  const [expiredAt, setExpiredAt] = useState('');
  const brand = useCurrentBrand();
  const { promoMeta, isPromoMetaLoading } = usePromoMeta();

  const { data: voucherResponse, isLoading: isVoucherLoading } = useTanstackQuery({
    queryKey: ['voucher', voucherId],
    queryFn: async () => {
      if (!voucherId) {
        return null;
      }
      return await getVoucher(voucherId);
    },
    enabled: Boolean(voucherId),
  });

  const voucher: Voucher | null = useMemo(() => {
    return voucherResponse?.result ?? null;
  }, [voucherResponse]);

  const voucherWarnings = useMemo(() => {
    if (!voucher) {
      return [];
    }
    const voucherData = voucher as VoucherApiFields;
    return voucherData.voucherWarnings ?? voucherData.voucher_warnings ?? [];
  }, [voucher]);

  // Populate form from API data — guarded to run only once per voucherId
  useEffect(() => {
    if (!isEdit) {
      reset(buildDefaultFormValues());
      setVoucherUsers([]);
      setExpiredAt('');
      setIncludeShowVoucherFromTime(false);
      return;
    }
    if (voucher) {
      const values = parseVoucherApiResponse(voucher);
      reset(values);
      setVoucherUsers([]);
      setIncludeShowVoucherFromTime(hasNonMidnightTime(values.showVoucherFromDate));
    }
  }, [isEdit, voucher, voucherId, reset]);

  const voucherNameValue = watch('voucherName');
  const voucherToggles = watch('voucherToggles');
  const voucherRewards = watch('voucherRewards');
  const voucherConditions = watch('voucherConditions');
  const currencyCode = watch('currencyCode');

  const voucherToggleOptions: VoucherToggleOption[] = promoMeta?.voucher_toggles ?? [];

  const selectedUnknownVoucherToggleOptions = useMemo<VoucherToggleOption[]>(() => {
    const knownToggleKeys = new Set(voucherToggleOptions.map((option) => option.key));
    return voucherToggles
      .filter((key) => !knownToggleKeys.has(key))
      .map((key) => ({
        key,
        isActive: false,
        internalDescription: `[UNKNOWN] ${key}`,
        internalDescriptionLong:
          'This toggle key is not in current voucher metadata and will be preserved unless explicitly removed.',
      }));
  }, [voucherToggles, voucherToggleOptions]);

  const voucherToggleOptionsWithUnknown = useMemo<VoucherToggleOption[]>(() => {
    const allOptions = [...voucherToggleOptions, ...selectedUnknownVoucherToggleOptions];
    return allOptions.sort((a, b) => {
      if (a.isBeingDeprecated !== b.isBeingDeprecated) {
        return Number(a.isBeingDeprecated ?? false) - Number(b.isBeingDeprecated ?? false);
      }
      return a.internalDescription.localeCompare(b.internalDescription);
    });
  }, [voucherToggleOptions, selectedUnknownVoucherToggleOptions]);

  const selectedVoucherToggleOptions = useMemo<VoucherToggleOption[]>(() => {
    const optionByKey = new Map(voucherToggleOptionsWithUnknown.map((option) => [option.key, option] as const));
    return voucherToggles
      .map((key) => optionByKey.get(key))
      .filter((option): option is VoucherToggleOption => Boolean(option));
  }, [voucherToggles, voucherToggleOptionsWithUnknown]);

  useEffect(() => {
    if (!isDirty) {
      return undefined;
    }
    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = '';
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [isDirty]);

  useEffect(() => {
    if (!isDirty) {
      return undefined;
    }
    const unblock = history.block(() => {
      const shouldLeave = window.confirm('You have unsaved changes. Are you sure you want to leave this page?');
      if (!shouldLeave) {
        return false;
      }
      return undefined;
    });
    return () => unblock();
  }, [isDirty, history]);

  useEffect(() => {
    const hash = location.hash.replace('#', '');
    if (hash === 'users') {
      const usersElement = document.getElementById('users');
      if (usersElement) {
        usersElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }
  }, [location.hash]);

  const createMutation = useMutation<CreateVoucherResponse, Error, CreateVoucherPayload>({
    mutationFn: async (payload) => createVoucher(payload),
  });

  const updateMutation = useMutation<UpdateVoucherResponse, Error, { id: string; payload: UpdateVoucherPayload }>({
    mutationFn: async ({ id, payload }) => updateVoucher(id, payload),
  });

  const createVoucherUsersMutation = useCreateVoucherUsers();

  const onSubmit = async (data: VoucherFormValues) => {
    try {
      const normalizedRewards = data.voucherRewards.map((reward): VoucherReward => {
        const minSpendProducts = (reward.min_spend_products ?? []).filter(Boolean);
        return {
          ...reward,
          currency_code: (reward.currency_code ||
            data.currencyCode ||
            'AUD') as unknown as VoucherReward['currency_code'],
          reward_amount: Number(reward.reward_amount),
          min_spend: reward.min_spend == null ? undefined : Number(reward.min_spend),
          ...(minSpendProducts.length > 0 ? { min_spend_products: minSpendProducts } : {}),
        };
      });

      let savedVoucherId = voucherId || '';
      const showVoucherFromDateValue = data.showVoucherFromDate.trim();

      if (isEdit) {
        const payload: UpdateVoucherPayload & {
          voucher_toggles?: CreateVoucherPayload['voucher_toggles'];
        } = {
          ...(data.voucherName.trim() ? { voucher_name: data.voucherName.trim() } : {}),
          voucher_title: data.voucherTitle,
          voucher_description: data.voucherDescription,
          voucher_rewards: normalizedRewards as UpdateVoucherPayload['voucher_rewards'],
          voucher_conditions: data.voucherConditions,
          voucher_toggles: data.voucherToggles as CreateVoucherPayload['voucher_toggles'],
          ...(showVoucherFromDateValue ? { show_voucher_from_date: showVoucherFromDateValue } : {}),
        };
        await updateMutation.mutateAsync({ id: voucherId || '', payload });
      } else {
        const payload: CreateVoucherPayload = {
          voucher_name: data.voucherName.trim(),
          voucher_title: data.voucherTitle,
          voucher_description: data.voucherDescription,
          voucher_rewards: normalizedRewards,
          voucher_conditions: data.voucherConditions,
          voucher_toggles: data.voucherToggles as CreateVoucherPayload['voucher_toggles'],
          ...(showVoucherFromDateValue ? { show_voucher_from_date: showVoucherFromDateValue } : {}),
        };
        const createResponse = await createMutation.mutateAsync(payload);
        savedVoucherId = createResponse?.result?.voucherId || '';
      }

      if (voucherUsers.length > 0 && savedVoucherId) {
        const resolvedExpiredAt = expiredAt.trim()
          ? normalizeToISODate(expiredAt)
          : normalizeToISODate(dayjs().add(1, 'year').format('YYYY-MM-DD'));
        await createVoucherUsersMutation.mutateAsync({
          voucher_id: savedVoucherId,
          expired_at: resolvedExpiredAt,
          voucher_users: voucherUsers.map((userId) => ({ user_id: userId })),
        });
      }

      reset(data);
      enqueueSnackbar(isEdit ? 'Updated voucher successfully' : 'Created voucher successfully', {
        variant: 'success',
      });

      queryClient.invalidateQueries({ queryKey: ['vouchers'] });
      if (isEdit && voucherId) {
        queryClient.invalidateQueries({ queryKey: ['voucher', voucherId] });
      }

      if (!isEdit && savedVoucherId) {
        history.replace(`/marketing/vouchers/${savedVoucherId}`);
      }
    } catch (error) {
      enqueueSnackbar(`Failed to save voucher: ${JSON.stringify(error, null, 4)}`, { variant: 'error' });
    }
  };

  if (isEdit && isVoucherLoading) {
    return <Spinner />;
  }

  return (
    <Container maxWidth="xl">
      <PageHeader title={isEdit ? 'Edit Voucher' : 'Create Voucher'}>
        <VoucherFormNavigation
          isSubmitting={isSubmitting}
          isEdit={isEdit}
          showAdvanceDescription={showAdvanceDescription}
          onBack={() => history.push('/marketing/vouchers')}
          onSubmit={handleSubmit(onSubmit)}
          onToggleAdvancedDescription={() => setShowAdvanceDescription(!showAdvanceDescription)}
        />
      </PageHeader>

      <Stack spacing={10}>
        {voucherWarnings.length > 0 && (
          <Stack direction="column" spacing={2}>
            {voucherWarnings.map((warning, index) => (
              <Alert
                key={`${warning.code ?? 'voucher-warning'}-${index}`}
                severity={getVoucherWarningSeverity(warning.level)}
                title={JSON.stringify(warning, null, 4)}
              >
                <AlertTitle>{warning.code ?? 'Voucher warning'}</AlertTitle>
                {warning.message || 'Please review this warning before proceeding.'}
              </Alert>
            ))}
          </Stack>
        )}
        <Stack spacing={2}>
          <PageSubheader title="Presentation" />
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ xs: 'stretch', sm: 'flex-start' }}>
            <Controller
              name="voucherName"
              control={control}
              rules={{
                validate: (value) => !!value.trim() || 'Voucher name is required',
              }}
              render={({ field, fieldState }) => (
                <TextField
                  {...field}
                  label="Voucher Name"
                  error={!!fieldState.error}
                  helperText={
                    fieldState.error?.message ||
                    (isEdit
                      ? 'Voucher name cannot be changed after creation'
                      : showAdvanceDescription
                      ? 'Name displayed for the voucher. This is used during processing and is a fallback value if the voucher title is not set'
                      : undefined)
                  }
                  fullWidth
                  required
                  disabled={isSubmitting}
                  slotProps={{
                    input: {
                      readOnly: isEdit,
                    },
                  }}
                />
              )}
            />
          </Stack>
          <Controller
            name="voucherTitle"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                label="Voucher Title"
                helperText={
                  showAdvanceDescription
                    ? 'Optional title displayed for this voucher to the user when available.'
                    : undefined
                }
                fullWidth
                disabled={isSubmitting}
              />
            )}
          />
          <Controller
            name="voucherDescription"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                label="Voucher Description"
                helperText={
                  showAdvanceDescription
                    ? 'Optional description shown to the user to explain voucher details.'
                    : undefined
                }
                fullWidth
                multiline
                rows={6}
                disabled={isSubmitting}
                sx={{
                  '& .MuiInputBase-inputMultiline': {
                    overflowY: 'auto',
                  },
                }}
              />
            )}
          />
          {!isPromoMetaLoading && promoMeta && promoMeta.voucher_toggles.length > 0 && (
            <Controller
              name="voucherToggles"
              control={control}
              render={({ field }) => (
                <Autocomplete
                  multiple
                  options={voucherToggleOptionsWithUnknown}
                  isOptionEqualToValue={(option, value) => option.key === value.key}
                  getOptionLabel={(option) =>
                    `${option.isBeingDeprecated ? '[DEPRECATED] - ' : ''}${!option.isActive ? '[INACTIVE] - ' : ''}${
                      option.internalDescription
                    }`
                  }
                  value={selectedVoucherToggleOptions}
                  onChange={(_event, newValue) => {
                    field.onChange(newValue.map((option) => option.key));
                  }}
                  renderInput={(params) => <TextField {...params} variant="outlined" label="Voucher Toggles" />}
                  renderOption={(props, option) => {
                    const prefix = !option.isActive ? '[INACTIVE] - ' : '';
                    const deprecatedPrefix = option.isBeingDeprecated ? '[DEPRECATED] - ' : '';
                    const label = `${deprecatedPrefix}${prefix}${option.internalDescription}`;
                    return (
                      <li
                        {...props}
                        title={
                          option.isActive
                            ? option.internalDescriptionLong
                            : `Inactive - ${option.internalDescriptionLong}`
                        }
                        style={option.isBeingDeprecated ? { textDecoration: 'line-through' } : undefined}
                      >
                        {label}
                      </li>
                    );
                  }}
                  renderTags={(value, getTagProps) =>
                    value.map((option, index) => (
                      <Chip
                        key={option.key}
                        {...getTagProps({ index })}
                        label={`${option.isBeingDeprecated ? '[DEPRECATED] - ' : ''}${
                          !option.isActive ? '[INACTIVE] - ' : ''
                        }${option.internalDescription}`}
                        title={
                          option.isActive
                            ? option.internalDescriptionLong
                            : `Inactive - this toggle will not be applied - ${option.internalDescriptionLong}`
                        }
                        sx={option.isBeingDeprecated ? { textDecoration: 'line-through' } : undefined}
                      />
                    ))
                  }
                  disabled={isSubmitting}
                />
              )}
            />
          )}
          <Stack direction="row" spacing={2} alignItems="flex-start">
            <Controller
              name="showVoucherFromDate"
              control={control}
              render={({ field }) => (
                <TextField
                  label="Show Voucher From Date"
                  type={includeShowVoucherFromTime ? 'datetime-local' : 'date'}
                  value={
                    field.value
                      ? includeShowVoucherFromTime
                        ? dayjs(field.value).format('YYYY-MM-DDTHH:mm')
                        : dayjs(field.value).format('YYYY-MM-DD')
                      : ''
                  }
                  onChange={(event) => {
                    const localValue = event.target.value;
                    if (!localValue) {
                      field.onChange('');
                      return;
                    }
                    field.onChange(dayjs(localValue).toISOString());
                  }}
                  InputLabelProps={{ shrink: true }}
                  helperText={
                    showAdvanceDescription
                      ? 'Optional date/time from which this voucher becomes visible to users. Leave empty for immediate visibility.'
                      : undefined
                  }
                  fullWidth
                  disabled={isSubmitting}
                />
              )}
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={includeShowVoucherFromTime}
                  onChange={(e) => {
                    const checked = e.target.checked;
                    setIncludeShowVoucherFromTime(checked);
                    const currentValue = getValues('showVoucherFromDate');
                    if (currentValue && !checked) {
                      setValue('showVoucherFromDate', dayjs(currentValue).startOf('day').toISOString(), {
                        shouldDirty: true,
                      });
                    }
                  }}
                  disabled={isSubmitting}
                />
              }
              label="Include time"
              sx={{ whiteSpace: 'nowrap', mt: 1 }}
            />
          </Stack>
        </Stack>
        <Box mt={3}>
          <PageSubheader title="Rewards">
            {showAdvanceDescription && (
              <Box component="span" color="text.secondary" alignItems="left">
                The amount the voucher is worth, and for which products. Only one reward is applied to an order.
              </Box>
            )}
          </PageSubheader>
          <Controller
            name="voucherRewards"
            control={control}
            render={({ field }) => (
              <VoucherRewards
                rewards={field.value}
                conditions={voucherConditions}
                onChange={field.onChange}
                currencyCode={currencyCode}
                disabled={isSubmitting}
              />
            )}
          />
        </Box>
        <Box mt={3}>
          <PageSubheader title="Conditions" />
          <Controller
            name="voucherConditions"
            control={control}
            render={({ field }) => (
              <VoucherConditions
                conditions={field.value}
                rewards={voucherRewards}
                onChange={field.onChange}
                showAdvanceDescription={showAdvanceDescription}
                disabled={isSubmitting}
              />
            )}
          />
        </Box>
        <VoucherUsers
          userIds={voucherUsers}
          onChange={setVoucherUsers}
          expiredAt={expiredAt}
          onExpiredAtChange={setExpiredAt}
          showAdvanceDescription={showAdvanceDescription}
          disabled={isSubmitting}
          voucherId={voucherId}
        />
        {isEdit && voucherNameValue && <VoucherRecentRequests voucherName={voucherNameValue} userId="" brand={brand} />}
        <Box mt={3}>
          <PageSubheader title="Debug" />
          <Stack direction="row" spacing={2} flexWrap="wrap">
            {isEdit && <DebugModal type="generic" title="Voucher API Response" data={voucherResponse?.result} />}
            <DebugModal type="generic" title="Promo Meta" data={promoMeta} />
          </Stack>
        </Box>
      </Stack>
    </Container>
  );
}
